import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  ColumnMovedEvent,
  ColumnResizedEvent,
  GridApi,
  GridReadyEvent,
  SortChangedEvent,
} from 'ag-grid-community';
import {
  emptyCustomization,
  type ColumnCustomization,
  type UserCustomizationSettings,
} from '../types/UserCustomization';
import {
  buildColumnStateFromCustomization,
  snapshotLiveLayout,
  withColumnPatch,
} from '../utils/applyCustomization';
import { useUserCustomization } from './useUserCustomization';

// Explicit-save model for per-user grid customization (v0.1.84).
//
//   saved         — what is persisted in Central dbo.UserCustomization
//   customization — what is applied on screen (saved + unsaved changes)
//   dirty         — customization differs from saved (status-bar button lights up)
//
// ANY change — native drag/resize/sort, the customization dialog, the
// right-click quick-format/summary items — lands in `customization` + dirty
// immediately; nothing is persisted until saveCustomizationNow() (the
// status-bar "שמירת התאמה אישית" button). Every change goes through a snapshot
// of the LIVE api.getColumnState() first, so applying/saving never drags the
// on-screen layout back to a stale order (the "שורת סיכום resets my drag" bug).
//
// All returned callbacks have stable identity (they read refs) — they are
// passed straight to <LopAgGrid>, whose props must be memoized.

const TOAST_MS = 2500;

type Override = {
  src: UserCustomizationSettings | null;
  val: UserCustomizationSettings | null;
  dirty: boolean;
};

export interface UseGridCustomizationResult {
  /** What the grid should render (saved + unsaved changes). Null = defaults. */
  customization: UserCustomizationSettings | null;
  dirty: boolean;
  toastVisible: boolean;
  /** Wire to <LopAgGrid onGridReady> (after setting its own gridApiRef). */
  onGridReady: (e: GridReadyEvent<any>) => void;
  onColumnMoved: (e: ColumnMovedEvent<any>) => void;
  onColumnResized: (e: ColumnResizedEvent<any>) => void;
  onSortChanged: (e: SortChangedEvent<any>) => void;
  /** Current customization with the live grid layout merged in — pass as
   *  `current` when opening the customization dialog. */
  snapshot: () => UserCustomizationSettings;
  /** Dialog "Save": apply on screen + mark dirty. Does NOT persist. */
  applyDraft: (next: UserCustomizationSettings) => Promise<void>;
  /** Right-click quick actions: patch one column, apply on screen + dirty. */
  quickCustomize: (colId: string, patch: Partial<ColumnCustomization>) => void;
  /** Dialog "Reset to defaults": explicit + destructive, so IMMEDIATE (DELETE row). */
  reset: () => Promise<void>;
  /** Status-bar button: persist the CURRENT live state, then toast. */
  saveCustomizationNow: () => Promise<void>;
}

export function useGridCustomization(
  resourceCode: string,
  gridApiRef: React.RefObject<GridApi<any> | null>,
): UseGridCustomizationResult {
  const { data: saved, save, reset: resetSaved } = useUserCustomization(resourceCode);
  const [ov, setOv] = useState<Override | null>(null);
  const [toastVisible, setToastVisible] = useState(false);
  const toastTimerRef = useRef<number | null>(null);

  // An override is only valid while it was derived from the current `saved`;
  // when saved changes (load / save / reset) the override is dropped.
  const live = ov && ov.src === saved ? ov : null;
  const customization = live ? live.val : saved;
  const dirty = live ? live.dirty : false;

  const customizationRef = useRef(customization);
  customizationRef.current = customization;
  const savedRef = useRef(saved);
  savedRef.current = saved;

  useEffect(
    () => () => {
      if (toastTimerRef.current != null) window.clearTimeout(toastTimerRef.current);
    },
    [],
  );

  // Customization changed (load, dialog apply, quick action, save, reset) →
  // push order/width/sort into the already-mounted grid. For changes that went
  // through snapshot() the order equals the live order, so this is a no-op for
  // layout and only matters for an explicit reorder in the dialog.
  useEffect(() => {
    const api = gridApiRef.current;
    if (api) pushToGridWith(api, customization);
  }, [customization, gridApiRef]);

  const onGridReady = useCallback(
    (e: GridReadyEvent<any>) => {
      // The page sets gridApiRef itself; make sure it is set before pushing.
      pushToGridWith(e.api, customizationRef.current);
    },
    [],
  );

  const markDirty = useCallback(() => {
    setOv((prev) => {
      const src = savedRef.current;
      if (prev && prev.src === src && prev.dirty) return prev;
      return { src, val: customizationRef.current, dirty: true };
    });
  }, []);

  const onColumnMoved = useCallback(
    (e: ColumnMovedEvent<any>) => {
      if (e.finished && e.source.startsWith('ui')) markDirty();
    },
    [markDirty],
  );
  const onColumnResized = useCallback(
    (e: ColumnResizedEvent<any>) => {
      if (e.finished && e.source.startsWith('ui')) markDirty();
    },
    [markDirty],
  );
  const onSortChanged = useCallback(
    (e: SortChangedEvent<any>) => {
      if (e.source.startsWith('ui')) markDirty();
    },
    [markDirty],
  );

  const snapshot = useCallback((): UserCustomizationSettings => {
    const api = gridApiRef.current;
    const base = customizationRef.current;
    if (!api) return base ?? emptyCustomization();
    return snapshotLiveLayout(base, api.getColumnState());
  }, [gridApiRef]);

  const applyDraft = useCallback(async (next: UserCustomizationSettings) => {
    setOv({ src: savedRef.current, val: next, dirty: true });
  }, []);

  const quickCustomize = useCallback(
    (colId: string, patch: Partial<ColumnCustomization>) => {
      const next = withColumnPatch(snapshot(), colId, patch);
      setOv({ src: savedRef.current, val: next, dirty: true });
    },
    [snapshot],
  );

  const reset = useCallback(async () => {
    await resetSaved();
    setOv(null);
    // Back to the page defaults — also reset the live layout (order/width/sort).
    gridApiRef.current?.resetColumnState();
  }, [resetSaved, gridApiRef]);

  const saveCustomizationNow = useCallback(async () => {
    const next = snapshot();
    await save(next);
    setToastVisible(true);
    if (toastTimerRef.current != null) window.clearTimeout(toastTimerRef.current);
    toastTimerRef.current = window.setTimeout(() => setToastVisible(false), TOAST_MS);
  }, [snapshot, save]);

  return {
    customization,
    dirty,
    toastVisible,
    onGridReady,
    onColumnMoved,
    onColumnResized,
    onSortChanged,
    snapshot,
    applyDraft,
    quickCustomize,
    reset,
    saveCustomizationNow,
  };
}

function pushToGridWith(api: GridApi<any>, settings: UserCustomizationSettings | null) {
  const lead = api.getColumn('ag-Grid-SelectionColumn') ? ['ag-Grid-SelectionColumn'] : [];
  const built = buildColumnStateFromCustomization(settings, lead);
  if (built) api.applyColumnState({ state: built.state, applyOrder: built.applyOrder });
}
